/**
 * Error handling utilities for standardizing error handling across the application
 */

export {};

// Define error types
export enum ErrorType {
  DATABASE = 'DATABASE_ERROR',
  AUTHENTICATION = 'AUTHENTICATION_ERROR',
  NETWORK = 'NETWORK_ERROR',
  VALIDATION = 'VALIDATION_ERROR',
  UNKNOWN = 'UNKNOWN_ERROR',
}

// Define error severity levels
export enum ErrorSeverity {
  INFO = 'INFO',
  WARNING = 'WARNING',
  ERROR = 'ERROR',
  CRITICAL = 'CRITICAL',
}

// Define the error context interface
export interface ErrorContext {
  operation: string;
  resource?: string;
  data?: Record<string, unknown>;
}

// Define the application error interface
export interface AppError {
  type: ErrorType;
  message: string;
  severity: ErrorSeverity;
  context?: ErrorContext;
  originalError?: unknown;
}

/**
 * Create a standardized application error
 */
export const createAppError = (
  type: ErrorType,
  message: string,
  severity: ErrorSeverity = ErrorSeverity.ERROR,
  context?: ErrorContext,
  originalError?: unknown
): AppError => {
  return {
    type,
    message,
    severity,
    context,
    originalError,
  };
};

/**
 * Log an error with standardized format
 */
export const logError = (error: AppError): void => {
  const { type, message, severity, context, originalError } = error;

  // Format the error message
  const formattedMessage = `[${severity}] ${type}: ${message}`;

  // Log with appropriate severity level
  switch (severity) {
    case ErrorSeverity.INFO:
      console.info(formattedMessage, { context, originalError });
      break;
    case ErrorSeverity.WARNING:
      console.warn(formattedMessage, { context, originalError });
      break;
    case ErrorSeverity.CRITICAL:
      console.error(formattedMessage, { context, originalError });
      // Could add additional handling for critical errors (e.g., reporting to a service)
      break;
    case ErrorSeverity.ERROR:
    default:
      console.error(formattedMessage, { context, originalError });
      break;
  }
};

/**
 * Higher-order function to handle database operations with standardized error handling
 */
export const withErrorHandling = async <T>(
  operation: () => Promise<T>,
  errorContext: ErrorContext,
  fallback?: T
): Promise<T> => {
  try {
    return await operation();
  } catch (error) {
    const appError = createAppError(
      ErrorType.DATABASE,
      `Error during ${errorContext.operation}`,
      ErrorSeverity.ERROR,
      errorContext,
      error
    );

    logError(appError);

    if (fallback !== undefined) {
      return fallback;
    }

    throw appError;
  }
};

/**
 * Higher-order function for database operations with retry logic
 */
export const withRetry = async <T>(
  operation: () => Promise<T>,
  errorContext: ErrorContext,
  maxRetries = 3,
  delayMs = 1000,
  fallback?: T
): Promise<T> => {
  let lastError: unknown;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;

      // Log the retry attempt
      const retryContext = {
        ...errorContext,
        data: {
          ...(errorContext.data || {}),
          attempt,
          maxRetries,
        },
      };

      const appError = createAppError(
        ErrorType.DATABASE,
        `Retry ${attempt}/${maxRetries} for ${errorContext.operation}`,
        attempt === maxRetries ? ErrorSeverity.ERROR : ErrorSeverity.WARNING,
        retryContext,
        error
      );

      logError(appError);

      // If this is not the last attempt, wait before retrying
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, delayMs * attempt));
      }
    }
  }

  // If we've exhausted all retries and still have an error
  if (fallback !== undefined) {
    return fallback;
  }

  throw createAppError(
    ErrorType.DATABASE,
    `Failed after ${maxRetries} attempts: ${errorContext.operation}`,
    ErrorSeverity.ERROR,
    errorContext,
    lastError
  );
};

/**
 * Higher-order function to handle authentication operations with standardized error handling
 */
export const withAuthErrorHandling = async <T>(
  operation: () => Promise<T>,
  errorContext: ErrorContext,
  fallback?: T
): Promise<T> => {
  try {
    return await operation();
  } catch (error) {
    const appError = createAppError(
      ErrorType.AUTHENTICATION,
      `Authentication error during ${errorContext.operation}`,
      ErrorSeverity.ERROR,
      errorContext,
      error
    );

    logError(appError);

    if (fallback !== undefined) {
      return fallback;
    }

    throw appError;
  }
};
